import { useState } from 'react';
import { Button, Flex, Page, PageHeader, Table, Text, TextField } from '@cmaas/ui';
import type { AdminDomainEvent } from '../../api/types';
import { usePage } from '../../api/usePage';

const SUBJECT_TYPES = ['pass', 'credential', 'reservation', 'event'] as const;

/** Cross-tenant guest-data feed (troubleshooting) — kept separate from the audit log (different table/meaning). */
export function DomainEventsPage() {
  const [tenantId, setTenantId] = useState('');
  const [subjectType, setSubjectType] = useState('');
  const [subjectId, setSubjectId] = useState('');

  const page = usePage<AdminDomainEvent>(
    ['admin-domain-events', tenantId, subjectType, subjectId],
    '/v1/admin/domain-events',
    {
      tenant_id: tenantId || undefined,
      subject_type: subjectType || undefined,
      subject_id: subjectId || undefined,
    },
  );
  const events = page.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <Page>
      <PageHeader title="Domain events" />
      <Flex gap="3" wrap="wrap">
        <TextField.Root
          placeholder="tenant_id (uuid)"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
        />
        <select value={subjectType} onChange={(e) => setSubjectType(e.target.value)}>
          <option value="">All subject types</option>
          {SUBJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <TextField.Root
          placeholder="subject_id (uuid)"
          value={subjectId}
          onChange={(e) => setSubjectId(e.target.value)}
        />
      </Flex>

      {page.isLoading ? (
        <Text as="p">Loading…</Text>
      ) : events.length === 0 ? (
        <Text as="p">No events match this filter.</Text>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Occurred at</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Tenant</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Subject</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Event type</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {events.map((e) => (
              <Table.Row key={e.id}>
                <Table.Cell>{new Date(e.occurred_at).toLocaleString()}</Table.Cell>
                <Table.Cell>{e.tenant_id}</Table.Cell>
                <Table.Cell>
                  {e.subject_type}:{e.subject_id}
                </Table.Cell>
                <Table.Cell>{e.event_type}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
      {page.hasNextPage && (
        <Button
          type="button"
          variant="soft"
          onClick={() => void page.fetchNextPage()}
          disabled={page.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </Page>
  );
}
